import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, Button, Input, Modal, Skeleton, Tabs, Space, DatePicker, Tooltip, Tour, Popconfirm, Table } from "antd";
import dayjs from "dayjs";
import Select from "react-select";
import WindowedSelect from "react-windowed-select";

let BASE_STATE_TASK = {
  description: null,
  comments: "",
  item: null,
  items_xref: null,
  task_status: null,
  task_statuses_xref: null,
  due_at: null,
  form_due_date: null,
  jobs_xref: null,
  owned_by: null,
  date_in: dayjs(),
};

/*
Prop api
open: boolean, 
client_id: int,
refresh: function
recurring: boolean
*/

function ModalViewTask(props) {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalWidth, setModalWidth] = useState(1200);

  const [task, setTask] = useState(BASE_STATE_TASK);

  const [editing, setEditing] = useState(true);
  const [company, setCompany] = useState(null);
  const [clients, setClients] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [users, setUsers] = useState([]);
  const [items, setItems] = useState([]);
  const [taskStatuses, setTaskStatuses] = useState([]);

  const focusFirst = useRef(null);

  const GetCompany = async () => {
    let response = await props.AxiosRequest("/api/companies/get");
    setCompany(response.data.company);
  };
  const GetClients = async () => {
    let response = await props.AxiosRequest("/api/clients/get/all");
    let mapped_clients = response.data.clients.map((m) => {
      return { ...m, value: m.id, label: m.name };
    });
    setClients(mapped_clients);
  };
  const GetJobs = async () => {
    let response = await props.AxiosRequest("/api/jobs/get", { client_id: task?.clients_xref });
    let mapped_jobs = response.data.jobs.map((m) => {
      return { ...m, value: m.id, label: m.name };
    });
    setJobs(mapped_jobs);
  };
  const GetTaskStatuses = async () => {
    let response = await props.AxiosRequest("/api/task-statuses/get");
    let statuses = response.data.task_statuses.map((m) => {
      return { ...m, label: m.name, value: m.id };
    });
    setTaskStatuses(statuses);
  };
  const GetUsers = async () => {
    let response = await props.AxiosRequest("/api/users/get");
    let mapped_users = response.data.users.map((m) => {
      return { ...m, value: m.id, label: m.name };
    });
    setUsers(mapped_users);
  };
  const GetItems = async () => {
    let response = await props.AxiosRequest("/api/items/get");
    let mapped_items = response.data.items.map((m) => {
      return { ...m, value: m.id, label: m.name };
    });
    setItems(mapped_items);
  };
  const GetTask = async () => {
    if (!props.viewTaskProps?.task_id || props.viewTaskProps?.task_id === task?.id) return;
    let response = await props.AxiosRequest("/api/tasks/get", { id: props.viewTaskProps?.task_id });
    setTask(
      response.data.tasks.map((m) => {
        return { ...m, due_at: dayjs(m.due_at), form_due_date: m.form_due_date ? dayjs(m.form_due_date) : null, date_in: dayjs(m.date_in) };
      })[0],
    );
  };
  const SetDefaultsFromTask = async () => {
    let found_client = props.MakeClientOptions(clients).find((f) => f.id === task.clients_xref);
    let found_job = jobs.find((f) => f.id === task.jobs_xref);
    let found_item = items.find((f) => f.id === task.items_xref);
    let found_user = users.find((f) => f.id === task.owned_by);
    let found_task_status = taskStatuses.find((f) => f.id === task.task_statuses_xref);
    setTask({ ...task, client: found_client, job: found_job, item: found_item, user: found_user, task_status: found_task_status });
  };
  const SaveTask = async () => {
    if (task?.description?.length === 0) return props.MakeNotification("info", "Please enter the task name.");
    if (!task.client || !task.job || !task.items_xref || !task.description || !task.task_statuses_xref || !task.due_at || !task.jobs_xref || !task.owned_by)
      return props.MakeNotification("info", "Please fill in all required fields.");
    await props.AxiosRequest("/api/tasks/update", { ...task });
    props.setViewTaskProps({ ...props.viewTaskProps, open: false, refresh: true });
  };

  useEffect(() => {
    setModalOpen(props.viewTaskProps?.open);
  }, [props.viewTaskProps?.open]);
  useEffect(() => {
    if (props.viewTaskProps?.editing !== null) setEditing(props.viewTaskProps?.editing);
  }, [props.viewTaskProps?.editing]);

  useEffect(() => {
    if (task?.id) {
      GetJobs();
    }
  }, [task?.id]);

  useEffect(() => {
    GetClients();
    GetItems();
    GetUsers();
    GetTaskStatuses();
    GetCompany();
  }, []);
  useEffect(() => {
    if (props.viewTaskProps?.task_id) GetTask();
  }, [props.viewTaskProps?.task_id, props?.viewTaskProps?.open]);
  useEffect(() => {
    if (task?.id) SetDefaultsFromTask();
  }, [task?.id, jobs]);

  return (
    <>
      <Modal
        afterOpenChange={(open) => {
          open && focusFirst.current?.select();
        }}
        title={editing ? "Edit Task" : "View Task"}
        centered
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          setTask(BASE_STATE_TASK);
          props.setViewTaskProps({ ...props.viewTaskProps, open: false, task_id: null });
        }}
        footer={
          <>
            <Button
              onClick={() => {
                setModalOpen(false);
                setTask(BASE_STATE_TASK);
                props.setViewTaskProps({ ...props.viewTaskProps, open: false });
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={() => {
                SaveTask();
              }}
            >
              Save Task
            </Button>
          </>
        }
        width={modalWidth}
      >
        {task ? (
          <Row gutter={[{ xs: 8, sm: 16, md: 16, lg: 16 }, 8]}>
            <Col span={8}>
              <label className="col-form-label-sm">Client *</label>
              <WindowedSelect
                isDisabled
                value={task.client}
                options={props.MakeClientOptions(clients)}
                onChange={(e) => {
                  setTask({ ...task, client: e, clients_xref: e.value });
                }}
              />
            </Col>
            <Col span={8}>
              <label className="col-form-label-sm">Job *</label>
              <Select
                isDisabled
                value={task.job}
                options={props.MakeJobOptions(jobs)}
                onChange={(e) => {
                  let found_item = items.find((f) => f.id === e.items_xref);
                  setTask({ ...task, job: e, jobs_xref: e.value, items_xref: found_item?.id, item: found_item });
                }}
              />
            </Col>
            <Col span={8}></Col>
            <Col span={8}>
              <label className="col-form-label-sm">Task Name *</label>
              <Input
                ref={focusFirst}
                value={task.description}
                placeholder="Bookkeeping, 1040, Quarterly Report, etc"
                onChange={(e) => {
                  setTask({ ...task, description: e.target.value });
                }}
              />
            </Col>
            <Col span={8}>
              <label className="col-form-label-sm">Item *</label>
              <Select
                value={task.item}
                options={items}
                onChange={(e) => {
                  setTask({ ...task, item: e, items_xref: e.value });
                }}
              />
            </Col>
            <Col span={8}>
              <label className="col-form-label-sm">Assignee *</label>
              <Select
                value={task.user}
                options={props.MakeUserOptions(users)}
                onChange={(e) => {
                  setTask({ ...task, user: e, owned_by: e.value });
                }}
              />
            </Col>
            <Col span={24}>
              <label className="col-form-label-sm">Comments</label>
              <Input.TextArea
                value={task.comments}
                onChange={(e) => {
                  setTask({ ...task, comments: e.target.value });
                }}
              />
            </Col>
            <Col span={6}>
              <label className="col-form-label-sm">Task Status *</label>
              <Select
                value={task.task_status}
                options={taskStatuses}
                onChange={(e) => {
                  setTask({ ...task, task_status: e, task_statuses_xref: e.value });
                }}
              />
            </Col>
            {!props?.addTaskProps?.recurring ? (
              <Col span={6}>
                <label className="col-form-label-sm">Date In *</label>
                <DatePicker
                  format={"MM/DD/YYYY"}
                  style={{ width: "100%" }}
                  allowClear={false}
                  value={task.date_in}
                  onChange={(e) => {
                    setTask({ ...task, date_in: e });
                  }}
                />
              </Col>
            ) : null}
            <Col span={6}>
              <label className="col-form-label-sm">{props?.viewTaskProps?.recurring ? "Next " : null}Due Date *</label>
              <DatePicker
                format={"MM/DD/YYYY"}
                style={{ width: "100%" }}
                allowClear={false}
                value={task.due_at}
                onChange={(e) => {
                  setTask({ ...task, due_at: e });
                }}
              />
            </Col>
            {!props.viewTaskProps?.recurring ? (
              <Col span={6}>
                <label className="col-form-label-sm">Form Due Date</label>
                <DatePicker
                  format={"MM/DD/YYYY"}
                  style={{ width: "100%" }}
                  value={task.form_due_date}
                  onChange={(e) => {
                    setTask({ ...task, form_due_date: e });
                  }}
                />
              </Col>
            ) : null}
            {props.viewTaskProps?.recurring ? (
              <Col span={6}>
                <label className="col-form-label-sm">Frequency *</label>
                <Select
                  value={task.frequency_object}
                  options={[
                    { value: "annually", label: "Annually" },
                    { value: "quarterly", label: "Quarterly" },
                    { value: "monthly", label: "Monthly" },
                    { value: "weekly", label: "Weekly" },
                    { value: "biweekly", label: "Biweekly" },
                  ]}
                  onChange={(e) => {
                    setTask({ ...task, frequency: e.value, frequency_object: e });
                  }}
                />
              </Col>
            ) : null}
          </Row>
        ) : (
          <Skeleton active />
        )}
      </Modal>
    </>
  );
}

export default ModalViewTask;
